import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Chip, Skeleton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import UndoIcon from "@mui/icons-material/Undo";

import {
  IPD_COLORS,
  IPD_SHAPE,
  IpdButton,
  IpdConfirmDialog,
  IpdSectionCard,
} from "../../ipd/utils";
import { formatDateTime } from "../constants";
import {
  listEmergencyCharges,
  postPendingCharges,
  reverseEmergencyCharges,
} from "../api/emergencyBillingApi";
import { CANCELLATION, chargeTotals, formatINR } from "../utils/charges";
import AddChargeDialog from "./AddChargeDialog";
import ChargeCheckSection from "./ChargeCheckSection";

/**
 * What this emergency visit has been billed for.
 *
 * Reversed charges stay on the list rather than disappearing: the backend
 * cancels audit-safely — nothing is deleted, a cancellation event backs the
 * total out — and a charge someone had to reverse is exactly the one a query
 * later will be about. The totals separate the three numbers for the same
 * reason: charged, reversed, and what actually stands.
 *
 * Emergency beds are not special here. A bed charge is an ordinary
 * service-catalogue row (`EMG-BED-*`), so it sits in this list alongside
 * everything else the visit consumed.
 */

function Total({ label, value, tone }) {
  return (
    <Box
      sx={{
        px: 1.75,
        py: 1.25,
        borderRadius: `${IPD_SHAPE.radiusMd}px`,
        border: `1px solid ${IPD_COLORS.border}`,
        bgcolor: IPD_COLORS.cardBg,
        minWidth: 130,
      }}
    >
      <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: IPD_COLORS.textMuted }}>
        {label}
      </Typography>
      <Typography
        sx={{
          mt: 0.25,
          fontSize: 17,
          fontWeight: 700,
          color: tone === "muted" ? IPD_COLORS.textMuted : IPD_COLORS.textPrimary,
        }}
      >
        {formatINR(value)}
      </Typography>
    </Box>
  );
}

export default function ChargesCard({ visit, disabled, onChanged }) {
  const visitId = visit.id;

  const [charges, setCharges] = useState([]);
  // Adding and reversing are money actions the server allows only some roles.
  const [canAdjust, setCanAdjust] = useState(false);
  const [loading, setLoading] = useState(true);
  // Bumped once each load has posted what was waiting; the charge check reads
  // on it, so it never shows rows that load is still posting.
  const [loadCount, setLoadCount] = useState(0);
  const [loadError, setLoadError] = useState("");

  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState("");

  const [reversing, setReversing] = useState(null);
  const [reason, setReason] = useState("");
  const [reverseBusy, setReverseBusy] = useState(false);
  const [reverseError, setReverseError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Opening the tab sends anything still waiting to billing first, so the
      // ledger shows it. A failure here is not the ledger's: it stays in the
      // charge check below with its reason.
      await postPendingCharges(visitId).catch(() => {});
      // A visit with no billing account comes back as an empty ledger rather
      // than an error, so nothing here has to tell the two apart.
      const { charges: rows, canAdjust: allowed } = await listEmergencyCharges(visitId);
      setCharges(rows);
      setCanAdjust(allowed);
      setLoadError("");
    } catch (e) {
      setLoadError(e?.message || "Could not load the charges on this visit");
    } finally {
      setLoading(false);
      setLoadCount((n) => n + 1);
    }
  }, [visitId]);

  useEffect(() => {
    load();
  }, [load]);

  const submitReversal = async () => {
    setReverseBusy(true);
    setReverseError("");
    try {
      await reverseEmergencyCharges(visitId, [reversing.id], reason.trim());
      setReversing(null);
      setReason("");
      await load();
      setNotice("Charge reversed.");
      onChanged?.();
    } catch (e) {
      // Most often "already on an issued invoice", which the clerk can act on —
      // so the server's own wording is shown rather than a generic failure.
      setReverseError(e?.message || "Could not reverse the charge");
    } finally {
      setReverseBusy(false);
    }
  };

  const totals = chargeTotals(charges);

  return (
    <IpdSectionCard
      title="Charges"
      subtitle="What this visit has been billed for"
      actions={
        !disabled && canAdjust && (
          <IpdButton
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              setNotice("");
              setAdding(true);
            }}
          >
            Add charge
          </IpdButton>
        )
      }
    >
      {notice && (
        <Alert
          severity="success"
          sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}
          onClose={() => setNotice("")}
        >
          {notice}
        </Alert>
      )}

      {/* What has not reached billing yet: refused charges and doses with no
          stocked item. Everything else is charged as it happens. */}
      <ChargeCheckSection
        visit={visit}
        refreshKey={loadCount}
        // No onChanged here: the visit page re-reads the visit by unmounting
        // this card, which would wipe the section's refusal reasons.
        onPosted={load}
      />

      {/* ------------------------------------------------------------ totals */}
      {!loading && !loadError && charges.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mb: 2 }}>
          <Total label="Charged" value={totals.gross} />
          <Total label="Reversed" value={totals.cancelled} tone="muted" />
          <Total label="Standing" value={totals.net} />
        </Box>
      )}

      {/* ------------------------------------------------------------- the list */}
      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {[0, 1].map((i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={58}
              sx={{ borderRadius: `${IPD_SHAPE.radiusMd}px` }}
            />
          ))}
        </Box>
      ) : loadError ? (
        <Alert
          severity="error"
          sx={{ borderRadius: `${IPD_SHAPE.radiusMd}px` }}
          action={
            <IpdButton variant="outlined" size="small" onClick={load}>
              Retry
            </IpdButton>
          }
        >
          {loadError}
        </Alert>
      ) : charges.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 3,
            px: 2,
            border: `1px dashed ${IPD_COLORS.borderHover}`,
            borderRadius: `${IPD_SHAPE.radiusMd}px`,
          }}
        >
          <ReceiptLongIcon sx={{ fontSize: 26, color: IPD_COLORS.textMuted }} />
          <Typography sx={{ mt: 1, fontSize: 14, fontWeight: 700 }}>
            Nothing charged yet
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 13, color: IPD_COLORS.textSecondary }}>
            {disabled
              ? "No charges were captured on this visit."
              : "Registration, consultation, bed, tests sent to the lab or radiology, doses given from pharmacy stock and recorded procedures are charged automatically as they happen. Add anything else the visit consumed here."}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {charges.map((c) => {
            const isReversal = c.kind === CANCELLATION;
            // A charge that no longer stands: either a cancellation was written
            // against it, or it was pulled back before it ever processed.
            const spent = isReversal || c.reversed || c.ignored;
            const tag = isReversal
              ? null
              : c.ignored
              ? "Ignored"
              : c.pending
              ? "Pending"
              : c.reversed
              ? "Reversed"
              : null;

            return (
              <Box
                key={c.id}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.25,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: `${IPD_SHAPE.radiusMd}px`,
                  border: `1px solid ${IPD_COLORS.border}`,
                  bgcolor: IPD_COLORS.cardBg,
                  opacity: spent ? 0.62 : 1,
                }}
              >
                {isReversal ? (
                  <UndoIcon sx={{ fontSize: 18, mt: 0.2, color: IPD_COLORS.error.text }} />
                ) : (
                  <ReceiptLongIcon sx={{ fontSize: 18, mt: 0.2, color: IPD_COLORS.primary }} />
                )}

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      flexWrap: "wrap",
                      gap: 0.75,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 13.5,
                        fontWeight: 700,
                        textDecoration: c.reversed ? "line-through" : "none",
                      }}
                    >
                      {isReversal ? "Reversal · " : ""}
                      {c.name || c.code}
                    </Typography>
                    {tag && (
                      <Chip
                        size="small"
                        label={tag}
                        sx={{
                          height: 20,
                          fontSize: 11,
                          fontWeight: 700,
                          color: c.pending ? IPD_COLORS.textMuted : IPD_COLORS.error.text,
                          bgcolor: "transparent",
                          border: `1px solid ${
                            c.pending ? IPD_COLORS.borderHover : IPD_COLORS.error.text
                          }`,
                        }}
                      />
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textMuted, mt: 0.25 }}>
                    {c.code}
                    {!isReversal && c.quantity ? ` · ×${c.quantity}` : ""}
                    {!isReversal && c.unitPrice !== null
                      ? ` · ${formatINR(c.unitPrice)} each`
                      : ""}
                    {c.chargedAt ? ` · ${formatDateTime(c.chargedAt)}` : ""}
                  </Typography>
                  {/* The reason lives on the cancellation, which is why a reversed
                      charge shows nothing here and its reversal row shows why. */}
                  {c.remarks && (
                    <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textPrimary, mt: 0.4 }}>
                      {c.remarks}
                    </Typography>
                  )}
                  {c.ignored && (
                    <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary, mt: 0.4 }}>
                      Pulled back before it reached the ledger.
                    </Typography>
                  )}
                </Box>

                <Typography
                  sx={{
                    fontSize: 13.5,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    mt: 0.2,
                    color: isReversal ? IPD_COLORS.error.text : IPD_COLORS.textPrimary,
                  }}
                >
                  {isReversal ? "−" : ""}
                  {formatINR(c.gross)}
                </Typography>

                {/* Only a live charge can be reversed. A cancellation is not a
                    charge, an ignored one never reached the ledger, and one
                    already reversed would be refused by the server anyway. */}
                {!disabled && !spent && canAdjust && (
                  <IpdButton
                    variant="outlined"
                    size="small"
                    startIcon={<UndoIcon sx={{ fontSize: 15 }} />}
                    onClick={() => {
                      setReason("");
                      setReverseError("");
                      setReversing(c);
                    }}
                  >
                    Reverse
                  </IpdButton>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      <AddChargeDialog
        open={adding}
        visit={visit}
        onClose={() => setAdding(false)}
        onCaptured={async (message) => {
          await load();
          if (message) setNotice(message);
          onChanged?.();
        }}
      />

      <IpdConfirmDialog
        open={Boolean(reversing)}
        destructive
        requireReason
        reasonLabel="Why is this being reversed?"
        reasonValue={reason}
        onReasonChange={setReason}
        title="Reverse this charge?"
        description={
          reversing ? (
            <>
              <strong>{reversing.name || reversing.code}</strong> ·{" "}
              {formatINR(reversing.gross)}. The charge is cancelled, not deleted — it
              stays on the ledger with your reason against it.
              {reverseError && (
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    mt: 1.25,
                    fontSize: 13,
                    fontWeight: 600,
                    color: IPD_COLORS.error.text,
                  }}
                >
                  {reverseError}
                </Box>
              )}
            </>
          ) : (
            ""
          )
        }
        confirmLabel="Reverse charge"
        loading={reverseBusy}
        onConfirm={submitReversal}
        onCancel={() => {
          setReversing(null);
          setReverseError("");
        }}
      />
    </IpdSectionCard>
  );
}
